import type { BoardModel } from "./boardModel";
import type { PlacedNode } from "./BoardNodes";
import {
  ENDPOINT_R,
  LABEL_FONT,
  nodeRadius,
  PATH_LABEL_FONT,
  PATH_LABEL_OPACITY,
} from "./constants";
import {
  edgeIndicator,
  focusCamera,
  FOCUS_MIN_SPAN_FRACTION,
  FOCUS_RECENT,
  onBoard,
  type EdgeIndicator,
} from "./focusFit";
import { labelFontSize, placeLabels, type PlacedLabel } from "./labels";
import {
  BOARD_LAYOUT_MODE,
  boardPadding,
  declumpPositions,
  fitCamera,
  MIN_SPAN_FRACTION,
  project,
  relaxPositions,
  usesDeclump,
  usesFocus,
  usesOptions,
  usesTightFocus,
  type BoardLayoutMode,
  type Camera,
  type RelaxNode,
  type Size,
} from "./layout";
import {
  fanOut,
  OPTION_LABEL_COLOR,
  OPTION_LABEL_FONT,
  OPTION_R,
} from "./options";

export interface BoardLabel extends PlacedLabel {
  opacity: number;
  /** Overrides the board's label colour (option labels are muted). */
  color?: string;
}

export interface BoardIndicator extends EdgeIndicator {
  endpoint: "start" | "target";
}

/** A next-word option placed on the board ("options" mode). */
export interface PlacedOption {
  id: string;
  px: number;
  py: number;
  r: number;
  /** Drawn by BoardNodes already (a path word or the target): spoke only. */
  existing: boolean;
}

export interface BoardLayout {
  camera: Camera;
  nodes: PlacedNode[];
  labels: BoardLabel[];
  /** Arrows at the board edge toward an off-screen start (focus fit). */
  indicators: BoardIndicator[];
  /** Next-word options of the current word; empty outside "options" mode. */
  options: PlacedOption[];
}

export interface LayoutOptions {
  mode?: BoardLayoutMode;
  /** A live game in progress: only then does the focus fit apply. */
  playing?: boolean;
  /** The player's path, oldest first (the focus fit frames its tail). */
  path?: string[];
}

type Pt = { x: number; y: number };

/** The board padding was tuned for 14px endpoints; grow it with them. */
const PAD_TUNED_R = 14;
export const fitPadding = (size: Size) =>
  boardPadding(size) + (ENDPOINT_R - PAD_TUNED_R);

function chooseCamera(
  model: BoardModel,
  size: Size,
  extent: number,
  focus: boolean,
  recent: Pt[],
  tight = false,
): Camera {
  const pad = fitPadding(size);
  const start = model.nodes.find((n) => n.endpoint === "start") ?? null;
  const target = model.nodes.find((n) => n.endpoint === "target");
  if (focus && target && recent.length > 0) {
    return focusCamera({
      recent,
      target,
      start,
      size,
      pad,
      minSpan: extent * FOCUS_MIN_SPAN_FRACTION,
      tight,
    }).camera;
  }
  return fitCamera(model.nodes, size, {
    pad,
    minSpan: extent * MIN_SPAN_FRACTION,
  });
}

/** Per node, the direction away from its linked neighbours. */
function awayFromNeighbours(
  pairs: [string, string][],
  nodes: RelaxNode[],
): Map<string, Pt> {
  const pos = new Map(nodes.map((n) => [n.id, n]));
  const away = new Map<string, Pt>();
  const add = (from: string, to: string) => {
    const a = pos.get(from);
    const b = pos.get(to);
    if (!a || !b || from === to) return;
    const d = Math.hypot(b.px - a.px, b.py - a.py) || 1;
    const v = away.get(from) ?? { x: 0, y: 0 };
    away.set(from, { x: v.x - (b.px - a.px) / d, y: v.y - (b.py - a.py) / d });
  };
  for (const [a, b] of pairs) {
    add(a, b);
    add(b, a);
  }
  return away;
}

/**
 * Screen-pixel layout for a board model: fit, relax, then label. Start,
 * target and current get 15px labels that are always placed; every other
 * word gets a 12px label at 70% opacity when a free slot exists. In
 * "options" mode the current word's options join the fit, the relaxation
 * (start, target and current stay pinned) and the labels (muted, 12px,
 * placed before the path labels so the two never collide).
 */
export function layoutBoard(
  model: BoardModel,
  size: Size,
  extent: number,
  opts: LayoutOptions = {},
): BoardLayout {
  const mode = opts.mode ?? BOARD_LAYOUT_MODE;
  const playing = !!opts.playing;
  const byId = new Map(model.nodes.map((n) => [n.id, n]));
  const currentNode = model.current ? byId.get(model.current) : undefined;
  const choices =
    usesOptions(mode) && playing && currentNode ? model.options : [];
  const focus = (usesFocus(mode) || usesOptions(mode)) && playing;
  const recent: Pt[] =
    choices.length > 0 && currentNode
      ? [currentNode, ...choices]
      : (opts.path ?? [])
          .slice(-FOCUS_RECENT)
          .map((w) => byId.get(w))
          .filter((n): n is NonNullable<typeof n> => !!n);
  const camera = chooseCamera(
    model,
    size,
    extent,
    focus,
    recent,
    usesTightFocus(mode),
  );
  const projected = model.nodes.map((n) => ({
    ...n,
    ...project(camera, n.x, n.y),
    r: nodeRadius(n),
  }));
  const cur = currentNode && project(camera, currentNode.x, currentNode.y);
  const projectedOptions: RelaxNode[] = choices
    .filter((o) => !o.existing)
    .map((o) => ({ id: o.id, ...project(camera, o.x, o.y), r: OPTION_R }));
  const fresh = cur ? fanOut(cur, projectedOptions) : projectedOptions;
  // Under the focus fit, words off the board stay where they project (their
  // edges run off the edge); only the words on the board are relaxed.
  const off = new Set(
    focus ? projected.filter((n) => !onBoard(n, size)).map((n) => n.id) : [],
  );
  const all: RelaxNode[] = [...projected, ...fresh];
  let moved: Map<string, RelaxNode>;
  if (usesDeclump(mode)) {
    // Start and target never move; with options, the current word holds
    // still too (it yields only to the start or target it would overlap).
    const pinned = new Set(
      projected.filter((n) => n.endpoint !== null).map((n) => n.id),
    );
    const soft = new Set(
      choices.length > 0 && model.current && !pinned.has(model.current)
        ? [model.current]
        : [],
    );
    const relaxed = declumpPositions(all, size, { pinned, soft, skip: off });
    moved = new Map(relaxed.map((n) => [n.id, n]));
  } else {
    moved = new Map(
      relaxPositions(
        all.filter((n) => !off.has(n.id)),
        size,
      ).map((n) => [n.id, n]),
    );
  }
  const at = <T extends RelaxNode>(n: T): T => {
    const m = moved.get(n.id);
    return m ? { ...n, px: m.px, py: m.py } : n;
  };
  const nodes: PlacedNode[] = projected.map(at);
  const freshPlaced = fresh.map(at);
  const pos = new Map<string, RelaxNode>(
    [...nodes, ...freshPlaced].map((n) => [n.id, n]),
  );
  const options: PlacedOption[] = choices.flatMap((o) => {
    const p = pos.get(o.id);
    return p
      ? [{ id: o.id, px: p.px, py: p.py, r: p.r, existing: o.existing }]
      : [];
  });

  const rank = (n: PlacedNode) =>
    n.endpoint === "target"
      ? 0
      : n.isCurrent
        ? 1
        : n.endpoint === "start"
          ? 2
          : 3;
  const shown = nodes
    .filter((n) => !off.has(n.id))
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (a.pathIndex < 0 ? 1e6 : a.pathIndex) -
          (b.pathIndex < 0 ? 1e6 : b.pathIndex),
    );
  const avail = size.w - 8;
  const request = (id: string, nominal: number, required: boolean) => ({
    id,
    text: id,
    fontSize: labelFontSize(nominal, id, avail),
    required,
  });
  const requests = [
    ...shown
      .filter((n) => rank(n) < 3)
      .map((n) => request(n.id, LABEL_FONT, true)),
    // Options before path labels: the choices keep their names.
    ...freshPlaced.map((n) => ({
      ...request(n.id, OPTION_LABEL_FONT, false),
      stepOut: true,
    })),
    ...shown
      .filter((n) => rank(n) >= 3)
      .map((n) => request(n.id, PATH_LABEL_FONT, false)),
  ];
  const mainIds = new Set(nodes.filter((n) => rank(n) < 3).map((n) => n.id));
  const freshIds = new Set(freshPlaced.map((n) => n.id));
  const declump = usesDeclump(mode);
  const pairs: [string, string][] = [
    ...model.links.map((l): [string, string] => [l.a, l.b]),
    ...options.map((o): [string, string] => [model.current ?? "", o.id]),
  ];
  const placeable = [...nodes, ...freshPlaced];
  const labels = placeLabels(placeable, requests, size, {
    eightSlots: declump,
    away: declump ? awayFromNeighbours(pairs, placeable) : undefined,
  }).map(
    (l): BoardLabel =>
      freshIds.has(l.id)
        ? { ...l, opacity: 1, color: OPTION_LABEL_COLOR }
        : { ...l, opacity: mainIds.has(l.id) ? 1 : PATH_LABEL_OPACITY },
  );
  const indicators: BoardIndicator[] = [];
  for (const n of nodes) {
    if (off.has(n.id) && n.endpoint) {
      indicators.push({
        ...edgeIndicator(n.id, n, size),
        endpoint: n.endpoint,
      });
    }
  }
  return { camera, nodes, labels, indicators, options };
}
